/**
 * Minimal fetch wrapper for backend API.
 *
 * - Automatically sends cookies (credentials: "include").
 * - Throws ApiError on non-2xx with the parsed body.
 * - Sends JSON request bodies, parses JSON responses.
 */

import i18n from "@/i18n";

export class ApiError extends Error {
  /**
   * True once a caller has read `detail` — i.e. is showing it to the user. The
   * global mutation fallback (App.tsx) reports only the errors nobody read, so
   * an action with no error handling no longer fails silently and one that has
   * it is not reported twice.
   */
  shown = false;
  private readonly _detail: string;

  constructor(
    public readonly status: number,
    detail: string,
    public readonly body: unknown,
  ) {
    super(`[${status}] ${detail}`);
    this._detail = detail;
  }

  get detail(): string {
    this.shown = true;
    return this._detail;
  }
}

/**
 * Extract a human-readable error message from a non-2xx response body.
 *
 * Handles FastAPI's three response shapes:
 *   - {"detail": "string"} — plain
 *   - {"detail": [{loc, msg, type}, ...]} — pydantic validation
 *   - {"detail": {message, reason}} — backend's _import_error_to_http
 *
 * Falls back to statusText so callers never get "" or "undefined".
 */
// The backend's English sentences an operator meets in everyday work — a wrong
// password, a duplicate name, a role refusal. Everything else is shown as sent.
const KNOWN_DETAILS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^Insufficient permissions$/, "common.role_forbidden"],
  [/^Invalid credentials$/, "common.err.invalid_credentials"],
  [/^Account temporarily locked due to too many failed attempts$/, "common.err.account_locked"],
  [/^Too many login attempts from this IP$/, "common.err.too_many_logins"],
  [/^This channel is already in the list$/, "common.err.channel_exists"],
  [/^This rule already exists$/, "common.err.rule_exists"],
  [/^Proxy with label '(?<label>.+)' already exists$/, "common.err.label_exists"],
  [/^Credential with label '(?<label>.+)' already exists$/, "common.err.label_exists"],
  [/^Username '(?<username>.+)' is already taken$/, "common.err.username_taken"],
  [/^Filter matched zero scraped_users/, "common.err.campaign_no_targets"],
];

function translateDetail(raw: string): string {
  for (const [pattern, key] of KNOWN_DETAILS) {
    const m = pattern.exec(raw);
    if (m) return i18n.t(key, m.groups ?? {});
  }
  return raw;
}

export function extractErrorDetail(body: unknown, statusText: string): string {
  if (typeof body === "object" && body !== null && "detail" in body) {
    const raw = (body as { detail: unknown }).detail;
    if (typeof raw === "string" && raw) return translateDetail(raw);
    if (Array.isArray(raw)) {
      return raw
        .map((e: unknown) =>
          typeof e === "object" && e !== null && "msg" in e
            ? `${(e as { loc?: unknown[] }).loc?.join(".") ?? ""}: ${String(
                (e as { msg: unknown }).msg,
              )}`
            : JSON.stringify(e),
        )
        .join("; ");
    }
    if (raw !== null && raw !== undefined && typeof raw === "object") {
      const obj = raw as Record<string, unknown>;
      if (typeof obj.message === "string" && obj.message) {
        return obj.reason ? `${obj.message} (${obj.reason})` : obj.message;
      }
      try {
        return JSON.stringify(raw);
      } catch {
        return String(raw);
      }
    }
  }
  if (typeof body === "string" && body) return body;
  return statusText || "Request failed";
}

const BASE = ""; // dev proxy handles /api → backend; prod = same origin

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.json !== undefined) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw new ApiError(response.status, extractErrorDetail(body, response.statusText), body);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, json?: unknown) => request<T>(path, { method: "POST", json }),
  put: <T>(path: string, json?: unknown) => request<T>(path, { method: "PUT", json }),
  patch: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: "PATCH", json }),
  delete: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: "DELETE", json }),
  /** Multipart POST — the browser sets its own content-type + boundary. */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),
};
